import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/stores";
import { DateRange } from "@/domains/common";
import { DashboardChartDateRangeData } from "@/domains/dashboard";
import { operatorDashboardSelects, operatorDashboardThunks } from "./store";
// components
import { Spin } from "antd";
import { DateRangeButtonGroup, TransferBarChart } from "@/components";

const formatChartData = (arrayData: DashboardChartDateRangeData[]) => {
  return arrayData.map((rawData) => {
    const datetime = dayjs(rawData.label);
    const hour = datetime.hour().toString().padStart(2, "0");
    return {
      name: datetime.format("MM/DD HH:mm"),
      count: rawData.value,
      hour,
    };
  });
};

export const OperatorHomePage = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(operatorDashboardSelects.loading);

  const dashboardData = useAppSelector(operatorDashboardSelects.data);

  useEffect(() => {
    void dispatch(operatorDashboardThunks.read());
  }, [dispatch]);

  const [dateRangeForTotal, setDateRangeForTotal] = useState<DateRange>(
    DateRange.day0
  );

  const [dateRangeForDeposit, setDateRangeForDeposit] = useState<DateRange>(
    DateRange.day0
  );

  const totalData = useMemo(() => {
    if (!dashboardData) return;
    return dashboardData.summary.total[dateRangeForTotal];
  }, [dashboardData, dateRangeForTotal]);

  const depositData = useMemo(() => {
    if (!dashboardData) return;
    return dashboardData.summary.deposit[dateRangeForDeposit];
  }, [dashboardData, dateRangeForDeposit]);

  const depositChartCounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.deposit_in_24_hours.counts);
  }, [dashboardData]);

  const depositChartAmounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.deposit_in_24_hours.amounts);
  }, [dashboardData]);

  const withdrawChartCounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.withdraw_in_24_hours.counts);
  }, [dashboardData]);

  const withdrawChartAmounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.withdraw_in_24_hours.amounts);
  }, [dashboardData]);

  return (
    <div className="flex flex:col gap:16">
      <Spin spinning={loading}>
        <div className="py:20 px:16 flex ai:flex-start gap:8 bg:white r:8">
          <div className="flex:1">
            <div className="f:bold fg:gray-48 t:center">營運概況</div>

            <DateRangeButtonGroup
              dateRange={dateRangeForTotal}
              onChange={setDateRangeForTotal}
            />

            <div className="w:full mt:24 flex gap:16">
              <div className="flex flex:1">
                <div className="w:80">代收顆數：</div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {Number.isInteger(totalData?.deposit)
                    ? totalData?.deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80">上存顆數：</div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(totalData?.tenant_deposit)
                    ? totalData?.tenant_deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.tenant_deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
            </div>
            <div className="w:full mt:24 flex gap:16">
              <div className="flex flex:1">
                <div className="w:80">代付顆數：</div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {Number.isInteger(totalData?.withdraw)
                    ? totalData?.withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80">下發顆數：</div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(totalData?.tenant_withdraw)
                    ? totalData?.tenant_withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.tenant_withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
            </div>
            <div className="w:full mt:24 flex gap:16">
              <div className="flex:1" />
              <div className="flex flex:1">
                <div className="w:80">總手續費：</div>
                <div className="flex:1 f:bold fg:warning t:right">
                  {Number.isInteger(totalData?.profit)
                    ? totalData?.profit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.profit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
            </div>
          </div>

          <div className="h:240 mr:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48">
              24 小時內代收單數
            </div>

            <TransferBarChart
              baseline={5}
              data={depositChartCounts}
              yAxisLabel={{ value: "單數" }}
            />
          </div>

          <div className="h:240 ml:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48">
              24 小時內代收顆數
            </div>

            <TransferBarChart
              baseline={5000}
              data={depositChartAmounts}
              yAxisLabel={{ value: "顆數" }}
            />
          </div>
        </div>
      </Spin>

      <Spin spinning={loading}>
        <div className="py:20 px:16 flex ai:flex-start gap:8 bg:white r:8">
          <div className="flex:1">
            <div className="f:bold fg:gray-48 t:center">代收統計</div>

            <DateRangeButtonGroup
              dateRange={dateRangeForDeposit}
              onChange={setDateRangeForDeposit}
            />

            <div className="w:4/5 mt:24 mx:auto flex gap:16">
              <div className="flex flex:1 mr:4">
                <div className="w:80">已開單數：</div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {depositData?.created.count.toLocaleString()}
                </div>
              </div>
              <div className="flex flex:1 ml:4">
                <div className="w:80">已開顆數：</div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(depositData?.created.amount)
                    ? depositData?.created.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : depositData?.created.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
            </div>

            <div className="w:4/5 mt:24 mx:auto flex gap:16">
              <div className="flex flex:1 mr:4">
                <div className="w:80">已繳單數：</div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {depositData?.arrived.count.toLocaleString()}
                </div>
              </div>
              <div className="flex flex:1 ml:4">
                <div className="w:80">已繳顆數：</div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(depositData?.arrived.amount)
                    ? depositData?.arrived.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : depositData?.arrived.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
            </div>
            <div className="w:4/5 mt:24 mx:auto flex gap:16">
              <div className="flex:1 mr:4" />
              <div className="flex flex:1 ml:4">
                <div className="w:80">繳費比率：</div>
                <div className="flex:1 f:bold fg:warning t:right">
                  {Number.isInteger(depositData?.rate)
                    ? depositData?.rate
                    : depositData?.rate.toFixed(2)}{" "}
                  %
                </div>
              </div>
            </div>
          </div>

          <div className="h:240 mr:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48">
              24 小時內代付單數
            </div>

            <TransferBarChart
              baseline={5}
              data={withdrawChartCounts}
              yAxisLabel={{ value: "單數" }}
            />
          </div>

          <div className="h:240 ml:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48">
              24 小時內代付顆數
            </div>

            <TransferBarChart
              baseline={5000}
              data={withdrawChartAmounts}
              yAxisLabel={{ value: "顆數" }}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
};
